import * as Breadcrumbs from './breadcrumbs';

/**
 * Installs the breadcrumb collectors ONCE: route changes and control clicks.
 * Imperative, so it needs no change to App.js — call it from index.js.
 *
 * Everything here is best-effort and wrapped: breadcrumbs must never break
 * navigation, a click, or the app.
 */

let installed = false;

/**
 * Label for a clicked control. Prefers stable identifiers over visible text,
 * because on-screen text can contain patient names. Whatever we pick still
 * goes through Breadcrumbs' sanitizer.
 */
export function controlLabel(target) {
	const el =
		target && target.closest
			? target.closest('button, a, [role="button"], input[type="submit"]')
			: null;
	if (!el) {
		return null;
	}

	const stable =
		el.getAttribute('data-testid') ||
		el.getAttribute('aria-label') ||
		el.getAttribute('name') ||
		el.getAttribute('id');
	if (stable) {
		return `${el.tagName.toLowerCase()}: ${stable}`;
	}

	// Fall back to short visible text only — long text is likely data, not a label.
	const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
	if (text && text.length <= 40) {
		return `${el.tagName.toLowerCase()}: ${text}`;
	}

	return el.tagName.toLowerCase();
}

/**
 * Record route changes without touching App.js: react-router navigates through
 * history, so wrap pushState/replaceState and listen for popstate.
 */
function installRouteBreadcrumbs() {
	const record = () => Breadcrumbs.nav(window.location.pathname);

	['pushState', 'replaceState'].forEach((method) => {
		const original = window.history[method];
		if (typeof original !== 'function') {
			return;
		}
		window.history[method] = function patched(...args) {
			const result = original.apply(this, args);
			try {
				record();
			} catch (_) {
				/* never break navigation */
			}
			return result;
		};
	});

	window.addEventListener('popstate', record);
	record(); // the landing page
}

export default function installBreadcrumbs() {
	if (installed || typeof window === 'undefined') {
		return;
	}
	installed = true;

	try {
		installRouteBreadcrumbs();

		document.addEventListener(
			'click',
			(event) => {
				try {
					const label = controlLabel(event.target);
					if (label) {
						Breadcrumbs.action(`click: ${label}`);
					}
				} catch (_) {
					/* never break a click */
				}
			},
			true
		);
	} catch (_) {
		/* breadcrumbs are best-effort */
	}
}
